import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, Share, TouchableOpacity, Platform } from 'react-native';
import { Card, Title, Paragraph, Button, Chip, useTheme, List, IconButton, Surface, Divider, ActivityIndicator } from 'react-native-paper';
import { MaterialIcons } from '@expo/vector-icons';
import { PlatformMap } from '../components/PlatformMap';
import { useRoute, useNavigation } from '@react-navigation/native';
import { reportGeneratorService } from '../services/reportGenerator';
import { FishingReport } from '../services/reportGenerator/types';
import { useDispatch } from 'react-redux';
import { setMapLocation } from '../store/mapSlice';
import { UnitConversions } from '../utils/unitConversions';

export default function ReportDetailScreen() {
  const theme = useTheme();
  const route = useRoute();
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const { reportId } = route.params as { reportId: string };
  
  const [report, setReport] = useState<FishingReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedHotspot, setSelectedHotspot] = useState<number | null>(null);
  const [mapRef, setMapRef] = useState<any>(null);
  const [mapCenter, setMapCenter] = useState({ lat: 27.8, lng: -97.0 });
  const [zoom, setZoom] = useState(7);
  const [initialMapSet, setInitialMapSet] = useState(false);
  const [currentRegion, setCurrentRegion] = useState<any>(null);
  const scrollViewRef = React.useRef<ScrollView>(null);
  const cardRefs = React.useRef<{ [key: number]: View | null }>({});
  
  useEffect(() => {
    loadReport();
  }, [reportId]);
  
  // Sync map position when selected hotspot changes
  useEffect(() => {
    if (selectedHotspot !== null && report) {
      const hotspot = report.fishingPredictions.hotspots[selectedHotspot];
      if (hotspot) {
        setMapCenter({ lat: hotspot.location.lat, lng: hotspot.location.lon });
        setZoom(10);
      }
    }
  }, [selectedHotspot, report]);
  
  useEffect(() => {
    if (report && !initialMapSet) {
      const region = (() => {
        if (report.fishingPredictions.hotspots.length > 0) {
          const lats = report.fishingPredictions.hotspots.map(h => h.location.lat);
          const lons = report.fishingPredictions.hotspots.map(h => h.location.lon);
          const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
          const centerLng = (Math.min(...lons) + Math.max(...lons)) / 2;
          
          // Calculate appropriate zoom level based on hotspot spread
          const latSpread = Math.max(...lats) - Math.min(...lats);
          const lonSpread = Math.max(...lons) - Math.min(...lons);
          const maxSpread = Math.max(latSpread, lonSpread);
          
          // Set zoom level based on spread, ensuring clustering is visible
          let calculatedZoom = 7; // Default zoom out level
          if (maxSpread < 0.5) calculatedZoom = 9;
          else if (maxSpread < 1) calculatedZoom = 8;
          else if (maxSpread < 2) calculatedZoom = 7;
          else if (maxSpread < 4) calculatedZoom = 6;
          else calculatedZoom = 5;
          
          setZoom(calculatedZoom);
          
          return { lat: centerLat, lng: centerLng };
        } else {
          return {
            lat: (report.requestParams.bounds.north + report.requestParams.bounds.south) / 2,
            lng: (report.requestParams.bounds.east + report.requestParams.bounds.west) / 2
          };
        }
      })();
      setMapCenter(region);
      setInitialMapSet(true);
    }
  }, [report, initialMapSet]);
  
  const loadReport = async () => {
    try {
      const reportData = await reportGeneratorService.getReport(reportId);
      setReport(reportData);
      // Report loaded with hotspots
    } catch (error) {
      console.error('Failed to load report:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const scrollToCard = (index: number) => {
    const cardRef = cardRefs.current[index];
    if (cardRef && scrollViewRef.current) {
      cardRef.measureLayout(
        scrollViewRef.current as any,
        (x, y) => {
          // Scroll to position the card near the top with some padding
          scrollViewRef.current?.scrollTo({ y: y - 100, animated: true });
        },
        () => {
          // Fallback if measureLayout fails
          console.log('MeasureLayout failed for card', index);
        }
      );
    }
  };
  
  const shareReport = async () => {
    if (!report) return;
    
    try {
      const message = `Fishing Report - ${new Date(report.generatedAt).toLocaleDateString()}\n\n` +
        `${report.executiveSummary.overall}\n\n` +
        `Top Hotspots:\n${ 
        report.fishingPredictions.hotspots.slice(0, 3).map((h, i) => 
          `${i + 1}. ${h.location.lat.toFixed(4)}°N, ${h.location.lon.toFixed(4)}°W - ${h.targetSpecies.join(', ')}`
        ).join('\n')}`;
        
      await Share.share({ message });
    } catch (error) {
      console.error('Share failed:', error);
    }
  };
  
  if (loading) {
    return (
      <View style={[styles.container, styles.centerContent]}>
        <ActivityIndicator size="large" />
        <Paragraph style={{ marginTop: 16 }}>Loading report...</Paragraph>
      </View>
    );
  }
  
  if (!report) {
    return (
      <View style={[styles.container, styles.centerContent]}>
        <MaterialIcons name="error-outline" size={48} color={theme.colors.error} />
        <Paragraph style={{ marginTop: 16 }}>Report not found</Paragraph>
        <Button mode="contained" onPress={() => navigation.goBack()} style={{ marginTop: 16 }}>
          Go Back
        </Button>
      </View>
    );
  }
  
  const mapRegion = (() => {
    if (report.fishingPredictions.hotspots.length > 0) {
      // Calculate bounds to include all hotspots
      const lats = report.fishingPredictions.hotspots.map(h => h.location.lat);
      const lons = report.fishingPredictions.hotspots.map(h => h.location.lon);
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLon = Math.min(...lons);
      const maxLon = Math.max(...lons);
      
      // Add padding to ensure all markers are visible
      const latPadding = (maxLat - minLat) * 0.2 || 0.5;
      const lonPadding = (maxLon - minLon) * 0.2 || 0.5;
      
      return {
        latitude: (minLat + maxLat) / 2,
        longitude: (minLon + maxLon) / 2,
        latitudeDelta: Math.max(maxLat - minLat + latPadding, 0.5),
        longitudeDelta: Math.max(maxLon - minLon + lonPadding, 0.5),
      };
    } else {
      return {
        latitude: (report.requestParams.bounds.north + report.requestParams.bounds.south) / 2,
        longitude: (report.requestParams.bounds.east + report.requestParams.bounds.west) / 2,
        latitudeDelta: Math.abs(report.requestParams.bounds.north - report.requestParams.bounds.south),
        longitudeDelta: Math.abs(report.requestParams.bounds.east - report.requestParams.bounds.west),
      };
    }
  })();
  
  return (
    <ScrollView 
      ref={scrollViewRef}
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      {/* Header */}
      <Surface style={styles.header}>
        <View style={styles.headerContent}>
          <IconButton
            icon="arrow-left"
            size={24}
            onPress={() => navigation.goBack()}
          />
          <Title style={styles.headerTitle}>Fishing Report</Title>
          <IconButton
            icon="share"
            size={24}
            onPress={shareReport}
          />
        </View>
      </Surface>
      
      {/* Executive Summary */}
      <Card style={styles.card}>
        <Card.Content>
          <Title>Executive Summary</Title>
          <Paragraph style={styles.summary}>
            {report.executiveSummary.overall}
          </Paragraph>
          
        </Card.Content>
      </Card>
      
      {/* Key Findings */}
      <Card style={styles.card}>
        <Card.Content>
          <Title style={styles.sectionTitle}>Key Findings</Title>
          {report.executiveSummary.keyFindings.map((finding, index) => (
            <View key={index} style={styles.findingItem}>
              <MaterialIcons name="check-circle" size={20} color={theme.colors.primary} />
              <Paragraph style={styles.findingText}>{finding}</Paragraph>
            </View>
          ))}
        </Card.Content>
      </Card>
      
      {/* Ocean Conditions */}
      <Card style={styles.card}>
        <Card.Content>
          <Title style={styles.sectionTitle}>Ocean Conditions</Title>
          <Paragraph style={styles.conditionsSummary}>
            {report.oceanConditions.summary}
          </Paragraph>
          
          <View style={styles.trendsContainer}>
            <View style={styles.trendItem}>
              <MaterialIcons 
                name="thermostat" 
                size={24} 
                color={theme.colors.primary} 
              />
              <Paragraph style={styles.trendLabel}>Temperature</Paragraph>
              <Chip compact>{report.oceanConditions.trends.temperature}</Chip>
            </View>
            
            <View style={styles.trendItem}>
              <MaterialIcons 
                name="waves" 
                size={24} 
                color={theme.colors.primary} 
              />
              <Paragraph style={styles.trendLabel}>Currents</Paragraph>
              <Chip compact>{report.oceanConditions.trends.currentStrength}</Chip>
            </View>
          </View>
          
          <Paragraph style={styles.featureCount}>
            {report.oceanConditions.features.length} ocean features detected
          </Paragraph>
        </Card.Content>
      </Card>
      
      {/* Hotspots Map */}
      {report.fishingPredictions.hotspots.length > 0 && (
        <Card style={styles.card}>
          <Card.Content>
            <Title style={styles.sectionTitle}>Fishing Hotspots Map</Title>
            <View style={styles.mapContainer}>
              <PlatformMap
                ref={mapRef}
                center={mapCenter}
                zoom={zoom}
                markers={report.fishingPredictions.hotspots.map((hotspot, index) => ({
                  position: { lat: hotspot.location.lat, lng: hotspot.location.lon },
                  icon: selectedHotspot === index ? '🎯' : '🎣',
                  size: [32, 32],
                  title: `Hotspot #${index + 1}: ${UnitConversions.formatTemperature(hotspot.waterConditions.temperature)} - ${hotspot.location.lat.toFixed(4)}°, ${hotspot.location.lon.toFixed(4)}°`
                }))}
                overlays={[]}
                shapes={[]}
                enableClustering={true}
                baseMapType="ocean"
                onMarkerClick={(position) => {
                  const index = report.fishingPredictions.hotspots.findIndex(
                    h => h.location.lat === position.lat && 
                         h.location.lon === position.lng
                  );
                  if (index !== -1) {
                    setSelectedHotspot(index);
                    setTimeout(() => {
                      scrollToCard(index);
                    }, 100);
                  }
                }}
              />
              <View style={styles.mapZoomControls}>
                <TouchableOpacity
                  style={styles.zoomButton}
                  onPress={() => setZoom(Math.min(zoom + 1, 18))}
                >
                  <MaterialIcons name="add" size={24} color="#000" />
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.zoomButton, { marginTop: 8 }]}
                  onPress={() => setZoom(Math.max(zoom - 1, 3))}
                >
                  <MaterialIcons name="remove" size={24} color="#000" />
                </TouchableOpacity>
              </View>
            </View>
          </Card.Content>
        </Card>
      )}
      
      {/* Detailed Hotspots */}
      <Card style={styles.card}>
        <Card.Content>
          <Title style={styles.sectionTitle}>
            Top {report.fishingPredictions.hotspots.length} Fishing Locations
          </Title>
          
          {report.fishingPredictions.hotspots.map((hotspot, index) => (
            <View 
              key={index}
              ref={(ref) => { cardRefs.current[index] = ref; }}
              collapsable={false}
            >
              <Card 
                style={[
                  styles.hotspotCard,
                  selectedHotspot === index && styles.selectedHotspot
                ]}
                onPress={() => {
                  setSelectedHotspot(index);
                  // Update map center to this location
                  setMapCenter({ lat: hotspot.location.lat, lng: hotspot.location.lon });
                  setZoom(10);
                  // Scroll to show the mini map properly centered
                  // Position the map in the middle of the screen
                  scrollViewRef.current?.scrollTo({ y: 300, animated: true });
                }}
            >
              <Card.Content>
                <View style={styles.hotspotHeader}>
                  <Title style={styles.hotspotTitle}>Location #{index + 1}</Title>
                </View>
                
                <View style={styles.coordinatesContainer}>
                  <MaterialIcons name="location-on" size={20} color={theme.colors.primary} />
                  <Paragraph style={styles.coordinates}>
                    {hotspot.location.lat.toFixed(4)}°N, {hotspot.location.lon.toFixed(4)}°W
                  </Paragraph>
                </View>
                
                <View style={styles.hotspotDetails}>
                  <View style={styles.detailRow}>
                    <MaterialIcons name="thermostat" size={18} color={theme.colors.onSurfaceVariant} />
                    <Paragraph style={styles.detailText}>
                      Water Temp: {UnitConversions.formatTemperature(hotspot.waterConditions.temperature)}
                    </Paragraph>
                  </View>
                  
                  {hotspot.waterConditions.currentSpeed !== undefined && (
                    <View style={styles.detailRow}>
                      <MaterialIcons name="waves" size={18} color={theme.colors.onSurfaceVariant} />
                      <Paragraph style={styles.detailText}>
                        Current: {UnitConversions.formatCurrentSpeed(hotspot.waterConditions.currentSpeed)} @ {hotspot.waterConditions.currentDirection?.toFixed(0)}°
                      </Paragraph>
                    </View>
                  )}
                  
                  {hotspot.waterConditions.gradients && hotspot.waterConditions.gradients.length > 0 && (
                    <View style={{ marginTop: 4 }}>
                      <Paragraph style={styles.gradientTitle}>Gradients:</Paragraph>
                      {hotspot.waterConditions.gradients.map((gradient, idx) => (
                        <Paragraph key={idx} style={styles.gradientText}>
                          • {gradient}
                        </Paragraph>
                      ))}
                    </View>
                  )}
                  
                </View>
                
                <Divider style={{ marginVertical: 8 }} />
                
                <View>
                  <Paragraph style={styles.reasonsTitle}>Why this spot:</Paragraph>
                  {hotspot.reasons.map((reason, idx) => (
                    <Paragraph key={idx} style={styles.reasonText}>
                      • {reason}
                    </Paragraph>
                  ))}
                </View>
              </Card.Content>
              </Card>
            </View>
          ))}
        </Card.Content>
      </Card>
      
      {/* Recommendations */}
      <Card style={styles.card}>
        <Card.Content>
          <Title style={styles.sectionTitle}>Recommendations</Title>
          
          {report.recommendations.primary.length > 0 && (
            <View style={styles.recommendationSection}>
              <Paragraph style={styles.recommendationTitle}>General Tips</Paragraph>
              {report.recommendations.primary.map((rec, index) => (
                <View key={index} style={styles.recommendationItem}>
                  <MaterialIcons name="lightbulb" size={18} color={theme.colors.primary} />
                  <Paragraph style={styles.recommendationText}>{rec}</Paragraph>
                </View>
              ))}
            </View>
          )}
          
          {report.recommendations.tackle.length > 0 && (
            <View style={styles.recommendationSection}>
              <Paragraph style={styles.recommendationTitle}>Tackle & Gear</Paragraph>
              {report.recommendations.tackle.map((rec, index) => (
                <View key={index} style={styles.recommendationItem}>
                  <MaterialIcons name="phishing" size={18} color={theme.colors.primary} />
                  <Paragraph style={styles.recommendationText}>{rec}</Paragraph>
                </View>
              ))}
            </View>
          )}
          
          {report.recommendations.safety.length > 0 && (
            <View style={styles.recommendationSection}>
              <Paragraph style={styles.recommendationTitle}>Safety</Paragraph>
              {report.recommendations.safety.map((rec, index) => (
                <View key={index} style={styles.recommendationItem}>
                  <MaterialIcons name="security" size={18} color={theme.colors.error} />
                  <Paragraph style={styles.recommendationText}>{rec}</Paragraph>
                </View>
              ))}
            </View>
          )}
        </Card.Content>
      </Card>
      
      {/* Data Quality */}
      <Card style={styles.card}>
        <Card.Content>
          <Title style={styles.sectionTitle}>Data Quality</Title>
          <View style={styles.dataQualityGrid}>
            <View style={styles.dataQualityItem}>
              <MaterialIcons name="cloud-queue" size={20} color={theme.colors.onSurfaceVariant} />
              <Paragraph style={styles.dataQualityLabel}>Cloud Coverage</Paragraph>
              <Paragraph style={styles.dataQualityValue}>{report.dataQuality.cloudCoverage}%</Paragraph>
            </View>
            
            <View style={styles.dataQualityItem}>
              <MaterialIcons name="update" size={20} color={theme.colors.onSurfaceVariant} />
              <Paragraph style={styles.dataQualityLabel}>Data Age</Paragraph>
              <Paragraph style={styles.dataQualityValue}>{report.dataQuality.dataAge}h</Paragraph>
            </View>
            
            <View style={styles.dataQualityItem}>
              <MaterialIcons name="grid-on" size={20} color={theme.colors.onSurfaceVariant} />
              <Paragraph style={styles.dataQualityLabel}>Resolution</Paragraph>
              <Paragraph style={styles.dataQualityValue}>{report.dataQuality.resolution}</Paragraph>
            </View>
            
            <View style={styles.dataQualityItem}>
              <MaterialIcons name="assessment" size={20} color={theme.colors.onSurfaceVariant} />
              <Paragraph style={styles.dataQualityLabel}>Completeness</Paragraph>
              <Paragraph style={styles.dataQualityValue}>{report.dataQuality.completeness.toFixed(0)}%</Paragraph>
            </View>
          </View>
        </Card.Content>
      </Card>
      
      {/* Report Metadata */}
      <Card style={[styles.card, { marginBottom: 32 }]}>
        <Card.Content>
          <Paragraph style={styles.metadata}>
            Report ID: {report.reportId}
          </Paragraph>
          <Paragraph style={styles.metadata}>
            Generated: {new Date(report.generatedAt).toLocaleString()}
          </Paragraph>
          <Paragraph style={styles.metadata}>
            Valid until: {new Date(report.validityPeriod.end).toLocaleDateString()}
          </Paragraph>
          <Paragraph style={styles.metadata}>
            Processing time: {(report.metadata.processingTime / 1000).toFixed(1)}s
          </Paragraph>
        </Card.Content>
      </Card>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centerContent: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    elevation: 2,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 8,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  card: {
    margin: 16,
    marginBottom: 0,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  summary: {
    fontSize: 16,
    lineHeight: 24,
    marginBottom: 16,
  },
  confidenceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  confidence: {
    fontSize: 14,
    fontWeight: '500',
  },
  findingItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 12,
  },
  findingText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
  conditionsSummary: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 16,
  },
  trendsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginVertical: 16,
  },
  trendItem: {
    alignItems: 'center',
    gap: 8,
  },
  trendLabel: {
    fontSize: 12,
    fontWeight: '500',
  },
  featureCount: {
    fontSize: 12,
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: 8,
  },
  mapContainer: {
    height: 300,
    borderRadius: 8,
    overflow: 'hidden',
    marginTop: 8,
  },
  map: {
    flex: 1,
  },
  hotspotCard: {
    marginBottom: 12,
    elevation: 1,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  selectedHotspot: {
    borderWidth: 2,
    borderColor: '#2196F3',
    elevation: 3,
  },
  hotspotHeader: {
    marginBottom: 12,
  },
  hotspotTitle: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  confidenceChip: {
    height: 28,
    alignSelf: 'flex-start',
    marginTop: 4,
  },
  coordinatesContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  coordinates: {
    fontSize: 14,
    fontWeight: '500',
  },
  hotspotDetails: {
    gap: 8,
    marginBottom: 12,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  detailText: {
    fontSize: 13,
  },
  reasonsTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  reasonText: {
    fontSize: 12,
    marginLeft: 8,
    lineHeight: 18,
  },
  gradientTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  gradientText: {
    fontSize: 12,
    marginLeft: 8,
    lineHeight: 18,
    color: '#4CAF50',
  },
  specialConditionTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  specialConditionText: {
    fontSize: 12,
    marginLeft: 8,
    lineHeight: 18,
    color: '#FF6B35',
    fontWeight: '500',
  },
  recommendationSection: {
    marginBottom: 16,
  },
  recommendationTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  recommendationItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 8,
  },
  recommendationText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 20,
  },
  dataQualityGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginTop: 8,
  },
  dataQualityItem: {
    flex: 1,
    minWidth: '45%',
    alignItems: 'center',
    gap: 4,
  },
  dataQualityLabel: {
    fontSize: 12,
    textAlign: 'center',
  },
  dataQualityValue: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  metadata: {
    fontSize: 12,
    color: '#666',
    marginBottom: 4,
  },
  calloutContainer: {
    padding: 10,
    minWidth: 150,
    maxWidth: 200,
  },
  calloutTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  calloutText: {
    fontSize: 12,
    marginBottom: 2,
  },
  calloutSpecies: {
    fontSize: 12,
    fontStyle: 'italic',
    color: '#666',
    marginTop: 4,
  },
  mapZoomControls: {
    position: 'absolute',
    bottom: 16,
    right: 16,
  },
  zoomButton: {
    backgroundColor: 'white',
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
});